import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import {
  QrCode, Users, UserCheck, Clock, MapPin, ArrowLeft,
  Maximize, Minimize, Settings, ChevronDown
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 5000;

function AnimatedCounter({ value, color }) {
  const [displayed, setDisplayed] = useState(value);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (value !== displayed) {
      setFlash(true);
      const timeout = setTimeout(() => {
        setDisplayed(value);
        setFlash(false);
      }, 400);
      return () => clearTimeout(timeout);
    }
  }, [value, displayed]);

  return (
    <span className={`transition-all duration-500 ${flash ? "scale-125 opacity-70" : "scale-100 opacity-100"}`} style={{ color, display: "inline-block" }}>
      {displayed}
    </span>
  );
}

export default function LiveScreenPage() {
  const { eventId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const containerRef = useRef(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/event/events/${eventId}/live-stats`, { headers });
      setData(res.data);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, [eventId, token]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const toggleFullscreen = () => {
    if (!fullscreen) {
      const el = containerRef.current || document.documentElement;
      if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
    } else {
      if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
    }
    setFullscreen(!fullscreen);
  };

  useEffect(() => {
    const handler = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl animate-pulse">Chargement de l'ecran Live...</div>
      </div>
    );
  }

  const { event, organization, stats, live_screen_config: cfg } = data;
  const primaryColor = organization?.primary_color || "#0099CC";
  const coverUrl = event?.cover_image_url ? `${API}${event.cover_image_url}` : null;

  const cards = [
    { key: "inscrits", label: "INSCRITS", value: stats.inscrits, color: primaryColor, icon: Users },
    { key: "presents", label: "PRESENTS", value: stats.presents, color: "#10B981", icon: UserCheck },
    { key: "attente", label: "EN ATTENTE", value: stats.attente, color: "#F59E0B", icon: Clock },
    { key: "places_dispo", label: "PLACES DISPONIBLES", value: stats.places_disponibles ?? "---", color: "#8B5CF6", icon: QrCode },
    { key: "capacite", label: "CAPACITE", value: stats.capacite || "---", color: "#64748b", icon: Users },
  ].filter(c => cfg?.[c.key] !== false);

  return (
    <div ref={containerRef} className="min-h-screen bg-black flex flex-col" data-testid="live-screen-page">
      {/* Controls bar (hidden in fullscreen) */}
      {!fullscreen && (
        <div className="flex items-center justify-between px-6 py-3 bg-[#0B0F19] border-b border-[#1E293B]">
          <button onClick={() => navigate(-1)} className="text-[#94A3B8] hover:text-white flex items-center gap-2 text-sm">
            <ArrowLeft className="h-4 w-4" /> Retour
          </button>
          <span className="text-white font-bold text-sm">Ecran Live</span>
          <button onClick={toggleFullscreen} className="text-[#94A3B8] hover:text-white flex items-center gap-2 text-sm" data-testid="live-fullscreen-btn">
            <Maximize className="h-4 w-4" /> Plein ecran
          </button>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-12 gap-8">
        {/* Event header */}
        <div className="flex flex-col sm:flex-row items-center gap-8 max-w-5xl w-full">
          {coverUrl && (
            <div className="w-48 sm:w-64 shrink-0">
              <img src={coverUrl} alt="Affiche" className="w-full rounded-2xl shadow-2xl" />
            </div>
          )}
          <div className="text-center sm:text-left flex-1">
            {organization?.logo_url && (
              <img src={organization.logo_url} alt="Logo" className="h-10 mb-3 mx-auto sm:mx-0" />
            )}
            <h1 className="text-3xl sm:text-5xl font-black text-white leading-tight">{event.title}</h1>
            <div className="flex flex-col sm:flex-row gap-4 mt-4 text-[#94A3B8] text-lg">
              {event.event_date && (
                <span className="flex items-center gap-2">
                  <Clock className="h-5 w-5" style={{ color: primaryColor }} />
                  {event.event_date} {event.start_time && `- ${event.start_time}`}
                </span>
              )}
              {event.location && (
                <span className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" style={{ color: primaryColor }} />
                  {event.location}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6 max-w-5xl w-full">
          {cards.map(card => (
            <div key={card.key} className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-6 sm:p-8 text-center" data-testid={`live-stat-${card.key}`}>
              <card.icon className="h-8 w-8 mx-auto mb-3 opacity-50" style={{ color: card.color }} />
              <p className="text-[#64748b] text-xs sm:text-sm font-bold uppercase tracking-wider mb-2">{card.label}</p>
              <p className="text-4xl sm:text-6xl font-black" style={{ color: card.color }}>
                <AnimatedCounter value={card.value} color={card.color} />
              </p>
            </div>
          ))}
        </div>

        {/* Presence rate */}
        {stats.inscrits > 0 && (
          <div className="text-center">
            <p className="text-[#64748b] text-sm mb-2">TAUX DE PRESENCE</p>
            <div className="w-64 h-3 bg-[#1E293B] rounded-full overflow-hidden mx-auto">
              <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${stats.taux_presence}%`, background: "#10B981" }} />
            </div>
            <p className="text-white text-2xl font-bold mt-2">{stats.taux_presence}%</p>
          </div>
        )}

        {/* Organization name */}
        <p className="text-[#2A3441] text-sm mt-auto">
          {organization?.name} — Propulse par GLOTECH Event Center
        </p>
      </div>

      {/* Fullscreen exit button */}
      {fullscreen && (
        <button onClick={toggleFullscreen} className="fixed top-4 right-4 z-50 p-3 bg-black/50 hover:bg-black/80 rounded-full text-white/50 hover:text-white transition-all" data-testid="live-exit-fullscreen">
          <Minimize className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
